/**
 * VARFlagDialog Component
 *
 * Shared dialog for toggling Red Flag and Sprinkler Flag on VAR entities.
 * Used from both the map popup path (via ToolContext/CustomEvent bridge)
 * and the explorer path (via local state in VARExplorer).
 *
 * Issue: #334
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import AppModal from '../../common/AppModal';
import { VAREntity, RedFlagStatus, SprinklerFlag } from '../../../types/var';
import { extractUserInfo } from '../../../utils/userContext';
import { logger } from '../../../utils/logger';
import { getBackendUrl } from '../../../services/configService';
import { todayInNwt } from '../../../utils/nwtDate';
import './VARFlagDialog.scss';

const log = logger('VARFlagDialog');

export type FlagType = 'redFlag' | 'sprinkler';

export interface VARFlagDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entity: VAREntity | null;
  flagType: FlagType;
  /** Called after a successful flag update with the updated entity */
  onSuccess?: (updated: VAREntity) => void;
}

const VARFlagDialog: React.FC<VARFlagDialogProps> = ({
  isOpen,
  onClose,
  entity,
  flagType,
  onSuccess,
}) => {
  // Determine current activation state
  const isCurrentlyActive = flagType === 'redFlag'
    ? entity?.red_flag_active ?? false
    : entity?.sprinklerFlag?.active ?? false;

  const actionLabel = isCurrentlyActive ? 'Deactivate' : 'Activate';
  const flagLabel = flagType === 'redFlag' ? 'Red Flag' : 'Sprinkler Protection';

  // Auto-populate user name from FusionAuth
  const { userInfo, isFetchingUserInfo } = useFusionAuth();

  // Form state for activation
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState<RedFlagStatus>('Active');
  const [createdBy, setCreatedBy] = useState('');
  const [activationDate, setActivationDate] = useState(() => todayInNwt());
  const [sprinklerName, setSprinklerName] = useState('');
  const [sprinklerDate, setSprinklerDate] = useState(() => todayInNwt());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-fill createdBy when userInfo becomes available
  useEffect(() => {
    if (!isFetchingUserInfo && userInfo) {
      const { userName } = extractUserInfo(userInfo);
      setCreatedBy(userName);
    }
  }, [userInfo, isFetchingUserInfo]);

  const resetForm = useCallback(() => {
    setReason('');
    setStatus('Active');
    setActivationDate(todayInNwt());
    setSprinklerName('');
    setSprinklerDate(todayInNwt());
    setError(null);
    // Re-populate createdBy from userInfo after reset
    if (userInfo) {
      const { userName } = extractUserInfo(userInfo);
      setCreatedBy(userName);
    } else {
      setCreatedBy('');
    }
  }, [userInfo]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!entity) return;

    setError(null);
    setSubmitting(true);

    try {
      let updates: Record<string, unknown>;

      if (flagType === 'redFlag') {
        if (isCurrentlyActive) {
          // Deactivate red flag
          updates = {
            red_flag_active: false,
            red_flag_status: null,
            red_flag_reason: null,
            red_flag_created_by: null,
          };
        } else {
          // Activate red flag — validate required fields
          if (!reason.trim()) { setError('Reason is required'); setSubmitting(false); return; }
          if (!activationDate) { setError('Activation date is required'); setSubmitting(false); return; }
          if (!createdBy.trim()) { setError('Created By is required'); setSubmitting(false); return; }
          updates = {
            red_flag_active: true,
            red_flag_status: status,
            red_flag_reason: reason.trim(),
            red_flag_created_by: createdBy.trim(),
            red_flag_date: activationDate,
          };
        }
      } else {
        // Sprinkler flag
        if (isCurrentlyActive) {
          // Deactivate sprinkler
          updates = {
            sprinklerFlag: {
              active: false,
              name: '',
              reason: '',
              sprinkerlocaldate: '',
            } as SprinklerFlag,
          };
        } else {
          // Activate sprinkler — validate required fields
          if (!sprinklerName.trim()) { setError('Name is required'); setSubmitting(false); return; }
          if (!reason.trim()) { setError('Reason is required'); setSubmitting(false); return; }
          if (!sprinklerDate) { setError('Date is required'); setSubmitting(false); return; }
          if (!createdBy.trim()) { setError('Created By is required'); setSubmitting(false); return; }
          updates = {
            sprinklerFlag: {
              active: true,
              name: sprinklerName.trim(),
              reason: reason.trim(),
              sprinkerlocaldate: sprinklerDate,
              created_by: createdBy.trim(),
            } as SprinklerFlag,
          };
        }
      }

      log.debug(`Updating VAR ${entity.id} ${flagType}`, updates);
      // Each flag has its own endpoint gated by its own permission
      // (var.flag_red / var.flag_sprinkler), so users without full
      // var.update can still toggle flags they're authorized for.
      const path = flagType === 'redFlag' ? 'red-flag' : 'sprinkler-flag';
      const url = `${getBackendUrl() || ''}/api/var/${encodeURIComponent(entity.id)}/${path}`;
      const res = await fetch(url, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Flag update failed (HTTP ${res.status}): ${text || res.statusText}`);
      }
      const json = await res.json();
      log.debug('Flag update successful', json);

      onSuccess?.((json?.data ?? json) as VAREntity);
      handleClose();
    } catch (err) {
      log.error('Flag update failed', err);
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSubmitting(false);
    }
  }, [entity, flagType, isCurrentlyActive, reason, status, createdBy, activationDate, sprinklerName, sprinklerDate, onSuccess, handleClose]);

  if (!entity) return null;

  return (
    <AppModal isOpen={isOpen} onClose={handleClose} width={440} persistent>
      <div className="var-flag-dialog">
        <h3 className="var-flag-dialog__title">
          {actionLabel} {flagLabel}
        </h3>
        <p className="var-flag-dialog__subtitle">
          {entity.name} ({entity.var_id})
        </p>

        {isCurrentlyActive ? (
          /* Deactivation — simple confirmation */
          <div className="var-flag-dialog__confirm">
            <p>Are you sure you want to deactivate the {flagLabel.toLowerCase()} for this VAR?</p>
          </div>
        ) : flagType === 'redFlag' ? (
          /* Red Flag Activation Form */
          <div className="var-flag-dialog__form">
            <label className="var-flag-dialog__field">
              <span className="var-flag-dialog__label">Reason *</span>
              <textarea
                className="var-flag-dialog__input var-flag-dialog__textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for red flag..."
                rows={3}
              />
            </label>
            <label className="var-flag-dialog__field">
              <span className="var-flag-dialog__label">Status *</span>
              <select
                className="var-flag-dialog__input"
                value={status}
                onChange={(e) => setStatus(e.target.value as RedFlagStatus)}
              >
                <option value="Active">Active</option>
                <option value="Resolved">Resolved</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </label>
            <label className="var-flag-dialog__field">
              <span className="var-flag-dialog__label">Activation Date *</span>
              <input
                className="var-flag-dialog__input"
                type="date"
                value={activationDate}
                onChange={(e) => setActivationDate(e.target.value)}
              />
            </label>
            <label className="var-flag-dialog__field">
              <span className="var-flag-dialog__label">Created By *</span>
              <input
                className="var-flag-dialog__input"
                type="text"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                placeholder="Your name"
              />
            </label>
          </div>
        ) : (
          /* Sprinkler Activation Form */
          <div className="var-flag-dialog__form">
            <label className="var-flag-dialog__field">
              <span className="var-flag-dialog__label">Name *</span>
              <input
                className="var-flag-dialog__input"
                type="text"
                value={sprinklerName}
                onChange={(e) => setSprinklerName(e.target.value)}
                placeholder="Sprinkler system name"
              />
            </label>
            <label className="var-flag-dialog__field">
              <span className="var-flag-dialog__label">Reason *</span>
              <textarea
                className="var-flag-dialog__input var-flag-dialog__textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for sprinkler activation..."
                rows={3}
              />
            </label>
            <label className="var-flag-dialog__field">
              <span className="var-flag-dialog__label">Date *</span>
              <input
                className="var-flag-dialog__input"
                type="date"
                value={sprinklerDate}
                onChange={(e) => setSprinklerDate(e.target.value)}
              />
            </label>
            <label className="var-flag-dialog__field">
              <span className="var-flag-dialog__label">Created By *</span>
              <input
                className="var-flag-dialog__input"
                type="text"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                placeholder="Your name"
              />
            </label>
          </div>
        )}

        {error && (
          <div className="var-flag-dialog__error">{error}</div>
        )}

        <div className="var-flag-dialog__actions">
          <button
            className="var-flag-dialog__btn var-flag-dialog__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className={`var-flag-dialog__btn var-flag-dialog__btn--${isCurrentlyActive ? 'deactivate' : 'activate'}`}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Updating...' : `${actionLabel} ${flagLabel}`}
          </button>
        </div>
      </div>
    </AppModal>
  );
};

export default VARFlagDialog;
